import { Box, ScrollArea, Select, Stack } from '@mantine/core';
import PermissionInput from 'components/inputs/permission-input/PermissionInput';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import { useEffect, useMemo, useState } from 'react';
import {
  isPermissionEnabledForOrganization,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';

type PermissionTemplateMap = Record<string, number[]>;
const NO_INHERITED_PERMISSIONS: number[] = [];

interface PermissionTemplateInputValue {
  templateName: string;
  permissions: number[];
}

interface PermissionTemplateInputProps {
  permissionType: 'organization' | 'department';
  value: PermissionTemplateInputValue;
  onChange: (value: PermissionTemplateInputValue) => void;
  moduleFlags: OrganizationModuleFlags;
  inheritedPermissions?: number[];
}

/**
 * Allows to select permission template and edit permissions.
 */
export default function PermissionTemplateInput({
  permissionType,
  value,
  onChange,
  moduleFlags,
  inheritedPermissions = NO_INHERITED_PERMISSIONS,
}: PermissionTemplateInputProps) {
  const [permissionTemplate, setPermissionTemplate] = useState(value.templateName);
  const {
    departmentPermissionTemplates,
    organizationPermissionTemplates,
    departmentPermissions,
    organizationPermissions,
  } = usePermissionDataProvider();
  const availablePermissionIds = useMemo(
    () =>
      new Set(
        (permissionType === 'department' ? departmentPermissions : organizationPermissions)
          .filter(({ slug }) => isPermissionEnabledForOrganization(slug, moduleFlags))
          .map(({ permissionId }) => permissionId)
      ),
    [departmentPermissions, moduleFlags, organizationPermissions, permissionType]
  );
  const inheritedPermissionIds = useMemo(() => new Set(inheritedPermissions), [inheritedPermissions]);
  const permissionTemplateMap = useMemo(
    () =>
      permissionType === 'department'
        ? departmentPermissionTemplates.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.slug]: curr.permissions
                .map((p) => p.permissionId)
                .filter(
                  (permissionId) =>
                    availablePermissionIds.has(permissionId) && !inheritedPermissionIds.has(permissionId)
                ),
            }),
            {} as PermissionTemplateMap
          )
        : permissionType === 'organization'
        ? organizationPermissionTemplates.reduce(
            (acc, curr) => ({
              ...acc,
              [curr.slug]: curr.permissions
                .map((p) => p.permissionId)
                .filter(
                  (permissionId) =>
                    availablePermissionIds.has(permissionId) && !inheritedPermissionIds.has(permissionId)
                ),
            }),
            {} as PermissionTemplateMap
          )
        : {},
    [
      availablePermissionIds,
      departmentPermissionTemplates,
      inheritedPermissionIds,
      organizationPermissionTemplates,
      permissionType,
    ]
  );

  const availableValuePermissions = useMemo(
    () => value.permissions.filter((permissionId) => availablePermissionIds.has(permissionId)),
    [availablePermissionIds, value.permissions]
  );

  const permissionTemplateSelectData: { value: string; label: string }[] = useMemo(
    () =>
      permissionType === 'department'
        ? departmentPermissionTemplates.map((template) => ({
            value: template.slug,
            label: template.permissionTemplateName,
          }))
        : permissionType === 'organization'
        ? organizationPermissionTemplates.map((template) => ({
            value: template.slug,
            label: template.permissionTemplateName,
          }))
        : [],
    [permissionType]
  );

  useEffect(() => {
    setPermissionTemplate(value.templateName);
  }, [value.templateName]);

  useEffect(() => {
    if (availableValuePermissions.length !== value.permissions.length) {
      onChange({ ...value, permissions: availableValuePermissions });
    }
  }, [availableValuePermissions, onChange, value]);

  /**
   * Handles template name change.
   */
  function handleTemplateNameChange(templateName: string) {
    setPermissionTemplate(templateName);

    if (templateName === 'no-template') {
      onChange({ ...value, templateName });
    } else {
      onChange({ templateName, permissions: permissionTemplateMap[templateName] });
    }
  }

  /**
   * Handles the permission change
   */
  function handlePermissionsChange(permissions: number[]) {
    onChange({ templateName: 'no-template', permissions });
  }

  return (
    <Stack spacing={24}>
      <Select
        searchable
        data={permissionTemplateSelectData}
        value={permissionTemplate}
        onChange={handleTemplateNameChange}
      />
      <ScrollArea h={400}>
        <Box pb={24}>
          <PermissionInput
            permissionType={permissionType}
            value={availableValuePermissions}
            onChange={handlePermissionsChange}
            moduleFlags={moduleFlags}
            inheritedPermissions={inheritedPermissions}
          />
        </Box>
      </ScrollArea>
    </Stack>
  );
}
